/**
 * Data loss prevention (PRD Phase 6.5.3–6.5.4, ADR-0022). An administrator
 * sets the organisation's rules — what to look for, whose mail, and what
 * happens when it is found — reads the report of what matched, and decides
 * on the messages a rule held for review.
 *
 * The same panels serve the platform console and webmail's Organisation
 * area, through `AdminWhereContext`.
 */

import { Fragment, useContext, useEffect, useState } from 'react';
import type {
  DlpAction,
  DlpDirection,
  DlpEvent,
  DlpFinding,
  DlpPolicy,
  DlpPolicySaved,
  DlpQuarantined,
  DlpQuarantinedMessage,
  DlpQuarantineList,
  DlpReport,
  DlpRule,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { AdminWhereContext, size } from './mailboxes';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

// ─── Words ───────────────────────────────────────────────────────────────────

export const DETECTORS: ReadonlyArray<[string, string]> = [
  ['card', 'Payment card numbers'],
  ['sg_nric', 'Singapore NRIC and FIN'],
  ['iban', 'Bank account numbers (IBAN)'],
  ['secret', 'Passwords, keys and tokens'],
];

/** Weakest first, as the API ranks them. */
export const ACTIONS: ReadonlyArray<[DlpAction, string, string]> = [
  ['audit', 'Record only', 'Sent as normal; the match is recorded in the report below.'],
  [
    'warn',
    'Warn the sender while writing',
    'The sender is told before sending and may send anyway.',
  ],
  [
    'encrypt',
    'Send only over an encrypted connection',
    'Delivered only if the receiving server accepts an encrypted connection.',
  ],
  [
    'quarantine',
    'Hold for an administrator',
    'Kept here, undelivered, until an administrator releases or deletes it.',
  ],
  ['block', 'Block', 'Refused, and the sender is told which rule stopped it.'],
];

const ACTION_BADGE: Record<string, [string, 'ok' | 'warn' | 'crit' | 'neutral']> = {
  audit: ['recorded', 'neutral'],
  warn: ['warned', 'warn'],
  encrypt: ['encrypted only', 'ok'],
  quarantine: ['held', 'warn'],
  block: ['blocked', 'crit'],
};

export function ActionBadge({ action }: { action: string }) {
  const [label, tone] = ACTION_BADGE[action] ?? [action, 'neutral'];
  return <Badge tone={tone}>{label}</Badge>;
}

const FINDING_WORDS: Record<string, string> = {
  card: 'payment card',
  sg_nric: 'NRIC/FIN',
  iban: 'IBAN',
  secret: 'password, key or token',
};

/** A masked sample, its run of dots shortened: "41••••••••••11" as "41••…11". */
export function shortMask(sample: string): string {
  return sample.replace(/•{3,}/g, '••…');
}

/** "payment card ×2 41••…11"; a custom pattern is named by its rule. */
export function findingInWords(f: DlpFinding): string {
  const what = FINDING_WORDS[f.kind] ?? f.kind.replace(/^custom:/, '');
  return `${what} ×${f.count}${f.sample ? ` ${shortMask(f.sample)}` : ''}`;
}

/** The first recipient and how many more. */
export function recipientsInWords(recipients: readonly string[]): string {
  if (recipients.length === 0) {
    return '—';
  }
  const more = recipients.length - 1;
  return more > 0 ? `${recipients[0]} +${more} more` : (recipients[0] ?? '—');
}

/** Comma, semicolon or space separated; blanks dropped. */
export function listOf(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((v) => v.trim())
    .filter((v) => v !== '');
}

// ─── The panel ───────────────────────────────────────────────────────────────

export function DlpPanel({ slug, active }: { slug: string; active: boolean }) {
  // The quarantine entry a report row pointed at.
  const [held, setHeld] = useState<string | null>(null);
  return (
    <>
      <DlpRulesPanel slug={slug} active={active} />
      <DlpReportPanel slug={slug} onShowHeld={setHeld} />
      {/* Decisions stay open on a suspended tenant: held mail still needs one. */}
      <DlpQuarantinePanel slug={slug} highlight={held} />
    </>
  );
}

// ─── Rules ───────────────────────────────────────────────────────────────────

/** A rule as the form holds it: lists as the text typed. */
interface RuleForm {
  readonly key: number;
  readonly id?: string;
  readonly name: string;
  readonly enabled: boolean;
  readonly detectors: readonly string[];
  readonly custom_pattern: string;
  readonly attachment_types: string;
  readonly min_count: string;
  readonly direction: DlpDirection;
  readonly senders: string;
  readonly recipient_domains: string;
  readonly action: DlpAction;
}

let nextKey = 0;

function formOf(rule: DlpRule): RuleForm {
  nextKey += 1;
  return {
    key: nextKey,
    ...(rule.id ? { id: rule.id } : {}),
    name: rule.name,
    enabled: rule.enabled,
    detectors: rule.detectors,
    custom_pattern: rule.custom_pattern ?? '',
    attachment_types: rule.attachment_types.join(', '),
    min_count: String(rule.min_count),
    direction: rule.direction,
    senders: rule.senders.join(', '),
    recipient_domains: rule.recipient_domains.join(', '),
    action: rule.action,
  };
}

/** The rule the API is sent; `position` is its place in the list. */
export function ruleOf(form: RuleForm, position: number): DlpRule {
  const count = form.min_count.trim();
  if (!/^\d+$/.test(count) || Number(count) < 1) {
    throw new Error(`${form.name || `Rule ${position + 1}`}: at least 1 match.`);
  }
  const pattern = form.custom_pattern.trim();
  return {
    ...(form.id ? { id: form.id } : {}),
    name: form.name.trim(),
    enabled: form.enabled,
    position,
    detectors: form.detectors,
    custom_pattern: pattern === '' ? null : pattern,
    attachment_types: listOf(form.attachment_types).map((t) => t.replace(/^\./, '')),
    min_count: Number(count),
    direction: form.direction,
    senders: listOf(form.senders),
    recipient_domains: listOf(form.recipient_domains).map((d) => d.replace(/^@/, '')),
    action: form.action,
  };
}

/** The starter: payment cards leaving the organisation, with a warning. */
function starter(taken: readonly RuleForm[]): RuleForm {
  const names = new Set(taken.map((r) => r.name.toLowerCase()));
  let name = 'Payment cards';
  for (let n = 2; names.has(name.toLowerCase()); n += 1) {
    name = `Payment cards ${n}`;
  }
  return formOf({
    name,
    enabled: true,
    position: taken.length,
    detectors: ['card'],
    custom_pattern: null,
    attachment_types: [],
    min_count: 1,
    direction: 'external',
    senders: [],
    recipient_domains: [],
    action: 'warn',
  });
}

function DlpRulesPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<DlpPolicy>(`${base(slug)}/dlp/rules`, { signal }),
    `dlp-rules:${slug}`,
  );
  return (
    <section className="adm-panel dlp">
      <h3>Data protection rules</h3>
      <p className="adm-muted">
        Mail your people send is checked as it is written and again as it is sent. When several
        rules match, the strongest action wins — record, warn, encrypt, hold, block — so the order
        below is only for reading. What matched is recorded masked, showing its first and last two
        characters, never the value itself.
      </p>
      <Loaded load={load}>
        {(policy) => (
          <RulesEditor
            key={JSON.stringify(policy.rules)}
            slug={slug}
            rules={policy.rules}
            active={active}
            onSaved={reload}
          />
        )}
      </Loaded>
    </section>
  );
}

function RulesEditor({
  slug,
  rules,
  active,
  onSaved,
}: {
  slug: string;
  rules: readonly DlpRule[];
  active: boolean;
  onSaved: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [forms, setForms] = useState<RuleForm[]>(() => rules.map(formOf));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const change = (next: RuleForm[]) => {
    setForms(next);
    setDirty(true);
    setSaved(null);
  };
  const update = (i: number, patch: Partial<RuleForm>) =>
    change(forms.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, by: -1 | 1) => {
    const next = [...forms];
    const [rule] = next.splice(i, 1);
    if (rule) {
      next.splice(i + by, 0, rule);
      change(next);
    }
  };

  const save = () => {
    let body: DlpRule[];
    try {
      body = forms.map(ruleOf);
    } catch (e) {
      setError(describeError(e));
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(null);
    api<DlpPolicySaved>(`${base(slug)}/dlp/rules`, { method: 'POST', body: { rules: body } })
      .then((done) => {
        setDirty(false);
        setSaved(
          done.published
            ? 'Policy saved.'
            : 'Policy saved. The mail store picks it up within a minute.',
        );
        onSaved();
      })
      .catch((e: unknown) => setError(describeError(e)))
      .finally(() => setBusy(false));
  };

  return (
    <>
      {forms.length === 0 ? (
        <p className="adm-muted">No rules: nothing is checked.</p>
      ) : (
        <ol className="dlp-rules">
          {forms.map((form, i) => (
            <li key={form.key}>
              <RuleFields
                form={form}
                index={i}
                last={i === forms.length - 1}
                active={active}
                onChange={(patch) => update(i, patch)}
                onMove={(by) => move(i, by)}
                onRemove={() => change(forms.filter((_, j) => j !== i))}
              />
            </li>
          ))}
        </ol>
      )}
      {active && (
        <div className="adm-inline">
          <button
            type="button"
            className="btn ghost"
            onClick={() => change([...forms, starter(forms)])}
          >
            Add a rule
          </button>
          <button type="button" className="btn" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save policy'}
          </button>
          {dirty && !busy && <span className="adm-muted adm-small">Unsaved changes</span>}
          {error && (
            <span className="adm-error" role="alert">
              {error}
            </span>
          )}
          {saved && (
            <span className="adm-ok" role="status">
              {saved}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function RuleFields({
  form,
  index,
  last,
  active,
  onChange,
  onMove,
  onRemove,
}: {
  form: RuleForm;
  index: number;
  last: boolean;
  active: boolean;
  onChange: (patch: Partial<RuleForm>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const explained = ACTIONS.find(([a]) => a === form.action)?.[2];
  return (
    <fieldset className="dlp-rule" aria-label={`Rule ${n}`} disabled={!active}>
      <div className="dlp-rule-head">
        <label className="dlp-name">
          Name
          <input
            value={form.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. Customer card numbers"
            required
          />
        </label>
        <label className="adm-check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
          />
          Enabled
        </label>
        {active && (
          <span className="adm-actions">
            <button
              type="button"
              className="adm-copy"
              aria-label={`Move rule ${n} up`}
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              Up
            </button>
            <button
              type="button"
              className="adm-copy"
              aria-label={`Move rule ${n} down`}
              disabled={last}
              onClick={() => onMove(1)}
            >
              Down
            </button>
            <button
              type="button"
              className="adm-copy"
              aria-label={`Remove rule ${n}`}
              onClick={onRemove}
            >
              Remove
            </button>
          </span>
        )}
      </div>

      <fieldset className="dlp-group">
        <legend>Look for</legend>
        <div className="dlp-detectors">
          {DETECTORS.map(([id, label]) => (
            <label key={id} className="adm-check">
              <input
                type="checkbox"
                checked={form.detectors.includes(id)}
                onChange={(e) =>
                  onChange({
                    detectors: e.target.checked
                      ? [...form.detectors, id]
                      : form.detectors.filter((d) => d !== id),
                  })
                }
              />
              {label}
            </label>
          ))}
        </div>
        <div className="adm-form-grid">
          <label>
            <span>
              Custom pattern <span className="adm-muted">(a regular expression)</span>
            </span>
            <input
              className="adm-mono"
              value={form.custom_pattern}
              spellCheck={false}
              onChange={(e) => onChange({ custom_pattern: e.target.value })}
              placeholder="(?i)project\s+falcon"
            />
          </label>
          <label>
            Attachment types
            <input
              value={form.attachment_types}
              spellCheck={false}
              onChange={(e) => onChange({ attachment_types: e.target.value })}
              placeholder="exe, js, zip"
            />
          </label>
          <label className="dlp-count">
            <span>At least</span>
            <input
              type="number"
              min={1}
              max={10000}
              value={form.min_count}
              aria-label="At least this many matches"
              onChange={(e) => onChange({ min_count: e.target.value })}
            />
            <span>{form.min_count === '1' ? 'match' : 'matches'}</span>
          </label>
        </div>
      </fieldset>

      <fieldset className="dlp-group">
        <legend>Applies to</legend>
        <div className="adm-inline dlp-direction">
          <label className="adm-check">
            <input
              type="radio"
              name={`dlp-direction-${form.key}`}
              checked={form.direction === 'external'}
              onChange={() => onChange({ direction: 'external' })}
            />
            Mail leaving the organisation
          </label>
          <label className="adm-check">
            <input
              type="radio"
              name={`dlp-direction-${form.key}`}
              checked={form.direction === 'all'}
              onChange={() => onChange({ direction: 'all' })}
            />
            All mail, including internal
          </label>
        </div>
        <div className="adm-form-grid">
          <label>
            <span>
              Senders <span className="adm-muted">(empty: everyone)</span>
            </span>
            <input
              value={form.senders}
              spellCheck={false}
              onChange={(e) => onChange({ senders: e.target.value })}
              placeholder="jane@acme.example, @finance.acme.example"
            />
          </label>
          <label>
            <span>
              Recipient domains <span className="adm-muted">(empty: any)</span>
            </span>
            <input
              value={form.recipient_domains}
              spellCheck={false}
              onChange={(e) => onChange({ recipient_domains: e.target.value })}
              placeholder="gmail.com, outlook.com"
            />
          </label>
        </div>
      </fieldset>

      <label className="dlp-action">
        Action
        <select
          value={form.action}
          onChange={(e) => onChange({ action: e.target.value as DlpAction })}
        >
          {ACTIONS.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {explained && <p className="adm-muted adm-small dlp-explain">{explained}</p>}
    </fieldset>
  );
}

// ─── Report ──────────────────────────────────────────────────────────────────

const DAYS = 30;

function DlpReportPanel({ slug, onShowHeld }: { slug: string; onShowHeld: (id: string) => void }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<DlpReport>(`${base(slug)}/dlp/report?days=${DAYS}`, { signal }),
    `dlp-report:${slug}`,
  );
  return (
    <section className="adm-panel dlp">
      <div className="adm-panel-head">
        <h3>What matched</h3>
        <button type="button" className="adm-copy" onClick={reload}>
          Refresh
        </button>
      </div>
      <Loaded load={load}>
        {(report) => <ReportBody report={report} onShowHeld={onShowHeld} />}
      </Loaded>
    </section>
  );
}

function ReportBody({
  report,
  onShowHeld,
}: {
  report: DlpReport;
  onShowHeld: (id: string) => void;
}) {
  const { summary, events } = report;
  const count = (stage: string, action: string) =>
    summary.by_action.find((r) => r.stage === stage && r.action === action)?.count ?? 0;
  const actions = ACTIONS.map(([a]) => a).filter((a) => count('compose', a) + count('send', a) > 0);
  const top = summary.by_rule.slice(0, 5);
  if (actions.length === 0 && events.length === 0) {
    return <p className="adm-muted">Nothing matched in the last {summary.days} days.</p>;
  }
  return (
    <>
      <div className="dlp-summary">
        <div>
          <h4>Last {summary.days} days</h4>
          <table className="adm-table dlp-counts">
            <thead>
              <tr>
                <th>Action</th>
                <th>While writing</th>
                <th>When sent</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a}>
                  <td>
                    <ActionBadge action={a} />
                  </td>
                  <td>{count('compose', a).toLocaleString()}</td>
                  <td>{count('send', a).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {top.length > 0 && (
          <div>
            <h4>Top rules</h4>
            <ol className="dlp-top adm-small">
              {top.map((r) => (
                <li key={r.rule}>
                  {r.rule} <span className="adm-muted">{r.count.toLocaleString()}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      {events.length > 0 && (
        <>
          <h4>Recent events</h4>
          <div className="adm-scroll">
            <table className="adm-table dlp-events">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Stage</th>
                  <th>Sender</th>
                  <th>Recipients</th>
                  <th>Subject</th>
                  <th>Action</th>
                  <th>Rules</th>
                  <th>Found</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <EventRow key={e.id} event={e} onShowHeld={onShowHeld} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function EventRow({ event: e, onShowHeld }: { event: DlpEvent; onShowHeld: (id: string) => void }) {
  const found = e.findings?.findings ?? [];
  const files = e.findings?.attachments ?? [];
  return (
    <tr>
      <td className="ed-nowrap">{when(e.at)}</td>
      <td className="ed-nowrap">{e.stage === 'compose' ? 'While writing' : 'Sent'}</td>
      <td>{e.sender}</td>
      <td title={e.recipients.join(', ') || undefined}>{recipientsInWords(e.recipients)}</td>
      <td>{e.subject || <span className="adm-muted">(no subject)</span>}</td>
      <td className="ed-nowrap">
        <ActionBadge action={e.action} />
        {e.quarantine_id && (
          <>
            {' '}
            <button
              type="button"
              className="adm-copy"
              onClick={() => e.quarantine_id && onShowHeld(e.quarantine_id)}
            >
              Held message
            </button>
          </>
        )}
      </td>
      <td>{e.rules.join(', ')}</td>
      <td className="dlp-found">
        {found.map((f) => (
          <div key={f.kind} className="adm-mono">
            {findingInWords(f)}
          </div>
        ))}
        {files.length > 0 && <div>Attachments: {files.join(', ')}</div>}
      </td>
    </tr>
  );
}

// ─── Quarantine ──────────────────────────────────────────────────────────────

const RELEASE = 'Send this message to its recipients unchanged?';
const DELETE = 'Delete this message? It will not be delivered, and cannot be recovered.';

function DlpQuarantinePanel({ slug, highlight }: { slug: string; highlight: string | null }) {
  const { base } = useContext(AdminWhereContext);
  const root = `${base(slug)}/dlp/quarantine`;
  const [load, reload] = useLoad(
    (signal) => api<DlpQuarantineList>(root, { signal }),
    `dlp-quarantine:${slug}`,
  );
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (highlight) {
      document.getElementById(`dlp-q-${highlight}`)?.scrollIntoView?.({ block: 'center' });
    }
  }, [highlight, load.state]);

  const decide = async (id: string, verb: 'release' | 'delete') => {
    if (!window.confirm(verb === 'release' ? RELEASE : DELETE)) {
      return false;
    }
    await api(`${root}/${encodeURIComponent(id)}/${verb}`, { method: 'POST' });
    reload();
    return true;
  };

  return (
    <section className="adm-panel dlp">
      <h3>Held for review</h3>
      <p className="adm-muted">
        Messages a rule held instead of delivering. Release sends one to its recipients as it was
        written; delete discards it. Every decision is recorded in the audit log. A message still
        held after 30 days is deleted.
      </p>
      <Loaded load={load}>
        {({ items }) => {
          const sorted = [...items].sort(
            (a, b) => Number(a.status !== 'held') - Number(b.status !== 'held'),
          );
          return sorted.length === 0 ? (
            <p className="adm-muted">Nothing is held.</p>
          ) : (
            <div className="adm-scroll">
              <table className="adm-table dlp-held">
                <thead>
                  <tr>
                    <th>Held</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Subject</th>
                    <th>Size</th>
                    <th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((item) => (
                    <HeldRow
                      key={item.id}
                      item={item}
                      on={item.id === highlight || item.id === open}
                      onReview={() => setOpen(item.id)}
                      onDecide={(verb) => decide(item.id, verb).then(() => undefined)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Loaded>
      {open && (
        <Review
          key={open}
          root={root}
          id={open}
          onClose={() => setOpen(null)}
          onDecide={async (verb) => {
            if (await decide(open, verb)) {
              setOpen(null);
            }
          }}
        />
      )}
    </section>
  );
}

function HeldRow({
  item,
  on,
  onReview,
  onDecide,
}: {
  item: DlpQuarantined;
  on: boolean;
  onReview: () => void;
  onDecide: (verb: 'release' | 'delete') => Promise<void>;
}) {
  return (
    <tr id={`dlp-q-${item.id}`} className={on ? 'on' : undefined}>
      <td className="ed-nowrap">{when(item.held_at)}</td>
      <td>{item.sender}</td>
      <td title={item.recipients.join(', ') || undefined}>{recipientsInWords(item.recipients)}</td>
      <td>{item.subject || <span className="adm-muted">(no subject)</span>}</td>
      <td className="ed-nowrap">{size(item.size_bytes)}</td>
      <td>
        {item.status === 'held' ? (
          <span className="adm-actions dlp-decide">
            <button type="button" className="btn ghost" onClick={onReview}>
              Review
            </button>
            <ActionButton run={() => onDecide('release')}>Release</ActionButton>
            <ActionButton tone="danger" run={() => onDecide('delete')}>
              Delete
            </ActionButton>
          </span>
        ) : (
          <span className="adm-small">
            <Badge tone={item.status === 'released' ? 'ok' : 'neutral'}>{item.status}</Badge>{' '}
            <span className="adm-muted">
              by {item.decided_by ?? '—'}, {when(item.decided_at)}
            </span>
          </span>
        )}
      </td>
    </tr>
  );
}

function Review({
  root,
  id,
  onClose,
  onDecide,
}: {
  root: string;
  id: string;
  onClose: () => void;
  onDecide: (verb: 'release' | 'delete') => Promise<void>;
}) {
  const [load] = useLoad(
    (signal) => api<DlpQuarantinedMessage>(`${root}/${encodeURIComponent(id)}`, { signal }),
    `dlp-held:${root}:${id}`,
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ed-review" role="dialog" aria-label="Review held message">
      <div className="adm-panel-head">
        <h3>Review</h3>
        <button type="button" className="btn ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <p className="adm-muted adm-small">Opening a message is recorded in the audit log.</p>
      <Loaded load={load}>
        {({ item, text, attachments }) => (
          <>
            <dl className="adm-facts ed-headers">
              {(
                [
                  ['From', item.sender],
                  ['To', item.recipients.join(', ')],
                  ['Subject', item.subject],
                  ['Held', when(item.held_at)],
                  ['Size', size(item.size_bytes)],
                ] as Array<[string, string | null]>
              )
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </Fragment>
                ))}
            </dl>
            <h4>Body text</h4>
            {text ? (
              <pre className="ed-body">{text}</pre>
            ) : (
              <p className="adm-muted adm-small">No text body.</p>
            )}
            <h4>Attachments</h4>
            {attachments.length === 0 ? (
              <p className="adm-muted adm-small">None.</p>
            ) : (
              <ul className="ed-attachments adm-small">
                {attachments.map((a, i) => (
                  <li key={i}>
                    {a.name ?? '(unnamed)'} <span className="adm-muted">{size(a.size)}</span>
                  </li>
                ))}
              </ul>
            )}
            {item.status === 'held' && (
              <div className="adm-inline">
                <ActionButton run={() => onDecide('release')}>Release</ActionButton>
                <ActionButton tone="danger" run={() => onDecide('delete')}>
                  Delete
                </ActionButton>
              </div>
            )}
          </>
        )}
      </Loaded>
    </div>
  );
}
